import { PropsWithChildren, useState } from 'react';
import { Box, BoxProps, Flex } from 'reflexbox';
import styled from 'styled-components';
import { rgba } from 'polished';

import { ChevronRightIcon } from '@treams/treams-icons';
import { Callout, Reorder, Skeleton } from '@treams/treams-ui';

import * as graphql from 'generated/graphql';

import GoalCard from './GoalCard';
import { useTranslation } from 'react-i18next';

const Expand = styled(Flex)<{ expanded?: boolean }>`
	border-radius: 10px;
	cursor: pointer;

	svg {
		display: block;
		width: 24px;
		height: 24px;
		transition: 1s cubic-bezier(0, 1, 0, 1) transform;

		${(props) => props.expanded && 'transform: rotateZ(90deg);'}

		path {
			fill: ${(props) => props.theme.colors.slateBlue};
		}
	}

	&:hover {
		background: ${(props) => rgba(props.theme.colors.lightBlue, 0.5)};
	}
`;

const ExpandIcon = styled(Box)<{ expanded?: boolean }>`
	margin: 0px -4px;

	svg {
		display: block;
		width: 24px;
		height: 24px;
		transition: 1s cubic-bezier(0, 1, 0, 1) transform;

		${(props) => props.expanded && 'transform: rotateZ(90deg);'}

		path {
			fill: ${(props) => props.theme.colors.slateBlue};
		}
	}
`;

export function GoalTreeChildren(props: {
	goal: graphql.Goal;
	user?: graphql.User;
	small?: boolean;
	inline?: boolean;
}) {
	const { t } = useTranslation();
	const query = graphql.useGoalChildrenQuery({ variables: { id: props.goal.id } });

	const recursive = query.data?.goal.children.edges.some(
		({ node }) => (node.children.totalCount ?? 0) > 0,
	);

	const ml = props.inline && !recursive ? 0 : 48;

	return (
		<Box ml={ml}>
			{query.data ? (
				<>
					{query.data.goal.children.edges.map(({ node }) => (
						<Box key={node.id} mt={2}>
							<GoalTree goal={node as graphql.Goal} user={props.user} small={props.small}>
								<GoalCard goal={node as graphql.Goal} user={props.user} small={props.small} />
							</GoalTree>
						</Box>
					))}
					{props.goal.children.totalCount &&
						query.data.goal.children.edges.length !== props.goal.children.totalCount && (
							<Callout
								mt={2}
								color="blue"
								title={t('goals.chilren.private.title')}
								description={t('goals.chilren.private.description', {
									count: props.goal.children.totalCount - query.data.goal.children.edges.length,
								})}
							/>
						)}
				</>
			) : (
				<Skeleton.Repeat count={props.goal.children.totalCount ?? 0}>
					<Box mt={2}>
						<GoalCard.Skeleton />
					</Box>
				</Skeleton.Repeat>
			)}
		</Box>
	);
}

export default function GoalTree(
	props: PropsWithChildren<{ goal: graphql.Goal; user?: graphql.User; small?: boolean } & BoxProps>,
) {
	const { goal, user, small, ...rest } = props;

	const [expanded, setExpanded] = useState(false);

	if (!props.goal.viewerCanViewChildren || (props.goal.children.totalCount ?? 0) === 0) {
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		return <Box {...(rest as any)}>{props.children}</Box>;
	}

	return (
		<>
			{/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
			<Flex alignItems="stretch" {...(rest as any)}>
				<Expand
					ml={-48}
					flexGrow={0}
					flexShrink={0}
					mr={2}
					alignItems="center"
					px={2}
					expanded={expanded}
					onClick={() => setExpanded(!expanded)}
				>
					<ChevronRightIcon />
				</Expand>
				<Box flexGrow={1} flexShrink={1}>
					{props.children}
				</Box>
			</Flex>
			{expanded && <GoalTreeChildren goal={goal} user={user} small={small} />}
		</>
	);
}

GoalTree.Reorder = function GoalTreeReorder(
	props: PropsWithChildren<
		{
			index: number;
			loading: boolean;
			goal: graphql.Goal;
			user?: graphql.User;
		} & BoxProps
	>,
) {
	const { goal, user, index, loading, ...rest } = props;

	const [expanded, setExpanded] = useState(false);

	return (
		<Reorder
			{...rest}
			id={goal.id}
			index={index}
			loading={loading}
			handle={(goal.children.totalCount ?? 0) === 0 ? undefined : <Box width={16} />}
			onHandleClick={
				(goal.children.totalCount ?? 0) === 0
					? undefined
					: () => setExpanded((expanded) => !expanded)
			}
		>
			<Flex alignItems="center">
				{goal.viewerCanViewChildren && (goal.children.totalCount ?? 0) > 0 && !loading && (
					<Box ml={-32} flexGrow={0} flexShrink={0} style={{ pointerEvents: 'none' }} mr={16}>
						<ExpandIcon expanded={expanded}>
							<ChevronRightIcon />
						</ExpandIcon>
					</Box>
				)}
				<Box flexGrow={1} flexShrink={1}>
					{props.children}
				</Box>
			</Flex>
			{expanded && <GoalTreeChildren goal={goal} user={user} small />}
		</Reorder>
	);
};
